import { useEffect, useRef, useState } from "react"

interface UseCustomScrollbarOptions {
    isActive?: boolean
    dependencies?: React.DependencyList
}

interface UseCustomScrollbarReturn {
    containerRef: React.RefObject<HTMLDivElement | null>
    thumbHeight: number
    thumbTop: number
    isScrollable: boolean //Whether scrolling is necessary (content overflows)
}

export function useCustomScrollbar({
    isActive = true,
    dependencies = []
}: UseCustomScrollbarOptions = {}): UseCustomScrollbarReturn {
    const containerRef = useRef<HTMLDivElement>(null)
    const [scrollTop, setScrollTop] = useState(0)
    const [scrollHeight, setScrollHeight] = useState(0)
    const [clientHeight, setClientHeight] = useState(0)

    useEffect(() => {
        if (!isActive) {
            setScrollTop(0)
            setScrollHeight(0)
            setClientHeight(0)
            return
        }

        // Wait for DOM to be ready
        const rafId = requestAnimationFrame(() => {
            const container = containerRef.current
            if (!container) {
                // If still not ready, try again next frame
                const retryId = requestAnimationFrame(() => {
                    const retryContainer = containerRef.current
                    if (!retryContainer) return

                    setupScrollListener(retryContainer)
                })
                return () => cancelAnimationFrame(retryId)
            }

            setupScrollListener(container)
        })

        function setupScrollListener(container: HTMLDivElement) {
            const updateScroll = () => {
                setScrollTop(container.scrollTop)
                setScrollHeight(container.scrollHeight)
                setClientHeight(container.clientHeight)
            }

            updateScroll()

            container.addEventListener("scroll", updateScroll, { passive: true })
            window.addEventListener("resize", updateScroll)
            const resizeObserver = new ResizeObserver(updateScroll)
            resizeObserver.observe(container)

            return () => {
                container.removeEventListener("scroll", updateScroll)
                window.removeEventListener("resize", updateScroll)
                resizeObserver.disconnect()
            }
        }

        return () => {
            cancelAnimationFrame(rafId)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isActive, ...dependencies])

    const maxScroll = scrollHeight - clientHeight
    const scrollProgress = maxScroll > 0 ? scrollTop / maxScroll : 0

    const thumbHeight =
        scrollHeight > clientHeight ? Math.min(scrollProgress * 80 + 20, 100) : 0

    const thumbTop = 0
    const isScrollable = scrollHeight > clientHeight

    return {
        containerRef: containerRef as React.RefObject<HTMLDivElement>,
        thumbHeight,
        thumbTop,
        isScrollable
    }
}
